import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
            <i className="ri-file-unknow-line text-4xl"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-foreground-600 mb-4">你访问的文章可能已被删除或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author.name,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    wordCount: article.wordCount,
    articleSection: article.categoryName,
    publisher: {
      '@type': 'Organization',
      name: 'po文网',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://arkfilm.cn' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://arkfilm.cn/category?cat=${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',po文网,原创文章'}
        canonicalUrl={`https://arkfilm.cn/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.coverImage}
        schema={articleSchema}
      />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen pt-16">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category?cat=${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-accent-100 text-accent-700 text-sm font-medium rounded-md whitespace-nowrap">
                {article.categoryName}
              </span>
              <span className="text-sm text-foreground-500">{article.readTime} 分钟阅读</span>
              <span className="text-sm text-foreground-500">{article.wordCount.toLocaleString()} 字</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                <img
                  src={article.author.avatar}
                  alt={article.author.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="font-medium text-sm text-foreground-900">{article.author.name}</div>
                <div className="text-xs text-foreground-500">
                  {article.publishDate} · {article.views.toLocaleString()} 阅读
                </div>
              </div>
            </div>
          </header>

          {/* Cover Image */}
          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8 bg-background-200">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none mb-10 article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-8 pt-6 border-t border-background-200">
            <span className="text-sm text-foreground-600">
              <i className="ri-price-tag-3-line mr-1"></i>标签：
            </span>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to="/category"
                className="px-3 py-1 bg-background-100 text-xs text-foreground-700 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-all duration-200 whitespace-nowrap"
              >
                {tag}
              </Link>
            ))}
          </div>

          {/* Engagement */}
          <div className="flex items-center justify-center gap-8 py-6 border-t border-b border-background-200 mb-10">
            <button className="flex items-center gap-2 text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200">
                <i className="ri-heart-line text-lg"></i>
              </div>
              <span className="text-sm font-medium">{article.likes}</span>
            </button>
            <button className="flex items-center gap-2 text-foreground-600 hover:text-accent-600 transition-colors duration-200 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 transition-colors duration-200">
                <i className="ri-chat-1-line text-lg"></i>
              </div>
              <span className="text-sm font-medium">{article.comments}</span>
            </button>
            <button className="flex items-center gap-2 text-foreground-600 hover:text-secondary-600 transition-colors duration-200 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 transition-colors duration-200">
                <i className="ri-bookmark-line text-lg"></i>
              </div>
              <span className="text-sm font-medium">收藏</span>
            </button>
            <button className="flex items-center gap-2 text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors duration-200">
                <i className="ri-share-forward-line text-lg"></i>
              </div>
              <span className="text-sm font-medium">分享</span>
            </button>
          </div>

          {/* Author Bio */}
          <div className="flex items-start gap-4 p-5 rounded-lg bg-background-100 mb-10">
            <div className="w-14 h-14 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
              <img
                src={article.author.avatar}
                alt={article.author.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="font-medium text-foreground-900 mb-1">{article.author.name}</div>
              <p className="text-sm text-foreground-600 leading-relaxed">{article.author.bio}</p>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}